import { supabase } from "./supabase";

// Acceso a las tablas de Supabase para las pantallas ya migradas. Las reglas RLS deciden qué puede ver y
// cambiar cada rol; aquí solo se arman las consultas y se traducen los errores a palabras de la iglesia.

// Columnas que se pueden guardar en cada tabla: los formularios traen además datos embebidos, fechas, etc.
const COLUMNAS = {
  miembros: ["familia_id", "nombres", "apellidos", "email", "telefono", "whatsapp", "fecha_nacimiento", "genero",
    "estado_civil", "estado", "fecha_ingreso", "fecha_bautismo", "como_llego", "quien_invito_id", "notas"],
  familias: ["apellido", "direccion", "barrio", "ciudad", "telefono", "notas"],
  bajas: ["miembro_id", "fecha_baja", "motivo", "detalle", "destino", "registrado_por_id"],
  visitas_nuevas: ["nombres", "apellidos", "telefono", "email", "direccion", "fecha_visita", "servicio_id",
    "invitado_por_id", "seguimiento", "convertido_miembro_id", "notas"],
  ninos: ["familia_id", "tutor_id", "nombres", "apellidos", "fecha_nacimiento", "genero", "alergias",
    "condicion_medica", "activo"],
  ediciones_curso: ["curso_id", "fecha_inicio", "fecha_fin", "dirigido_por_id", "estado", "notas"],
  inscripciones_curso: ["edicion_id", "miembro_id", "fecha_inscripcion", "completado", "fecha_completado", "notas"],
  asistencia_adultos: ["servicio_id", "miembro_id", "presente"],
  asistencia_ninos: ["servicio_id", "nino_id", "presente"],
  calificaciones_ninos: ["servicio_id", "nino_id", "asistio", "tarea", "biblia", "cuaderno", "notas"],
  tipos_servicio: ["nombre", "descripcion", "frecuencia", "dirigido_a"],
  servicios: ["tipo_id", "titulo", "fecha", "hora", "dirigido_por_id", "tema", "notas"],
  programaciones_servicio: ["servicio_id", "titulo_predica", "predicador", "version_biblica", "versiculos", "notas"],
  canciones_programacion: ["programacion_id", "orden", "cancion", "artista", "enlace"],
  areas_servicio: ["nombre", "descripcion", "lider_id", "activo", "es_infantil", "rotacion_automatica",
    "personas_por_reunion", "puestos_rotacion", "tipos_servicio_rotacion"],
  miembro_area: ["miembro_id", "area_id", "rol_en_area", "fecha_inicio", "activo"],
  turnos_servicio: ["servicio_id", "area_id", "miembro_id", "rol_en_turno", "notas", "confirmado", "rechazado",
    "motivo_rechazo"],
};

const MENSAJES = [
  [/miembros_email_unico/, "Ya hay otra persona registrada con ese correo"],
  [/miembros_email_normalizado/, "Escribe un correo válido"],
  [/miembros_estado_valido/, "Estado no válido"],
  [/areas_rotacion_con_reuniones/, "Elige en qué reuniones se usa el área para rotarla automáticamente"],
  [/areas_maximo_puestos/, "Un área puede tener hasta 20 puestos"],
  [/personas_por_reunion/, "Las personas por reunión deben estar entre 1 y 20"],
  [/acceso total/, "Solo quien tiene acceso total cambia si un área es de Mi Casa Kids"],
  [/duplicate key value.*(nombre|_key)/, "Ya existe un registro con ese nombre"],
  [/null value in column "email"/, "El correo es obligatorio"],
  [/null value in column "(\w+)"/, (m) => `Falta un dato obligatorio (${m[1].replace(/_/g, " ")})`],
  [/usuarios_miembro_id_fkey/, "Esta persona tiene una cuenta de acceso: elimínala primero en Configuración → Usuarios"],
  [/foreign key/, "Hay otros registros que dependen de este; no se puede eliminar"],
  [/row-level security|permission denied/, "No tienes permiso para realizar esta acción"],
  [/invalid input syntax for type (date|integer)/, "Revisa el formato de los datos"],
  [/fetch|network/i, "No hay conexión. Revisa tu internet e inténtalo de nuevo."],
];

// Los errores de la base llevan el nombre ErrorDeDatos: si una pantalla no los maneja, igual se avisan.
const errorDeDatos = (mensaje) => Object.assign(new Error(mensaje), { name: "ErrorDeDatos" });

export function traducirError(error, porDefecto = "Ocurrió un error. Inténtalo de nuevo.") {
  const texto = `${error?.message || ""} ${error?.details || ""}`;
  for (const [patron, mensaje] of MENSAJES) {
    const m = texto.match(patron);
    if (m) return errorDeDatos(typeof mensaje === "function" ? mensaje(m) : mensaje);
  }
  return errorDeDatos(error?.message || porDefecto);
}

function revisar({ data, error }) {
  if (error) throw traducirError(error);
  return data;
}

// Solo columnas de la tabla; un texto vacío se guarda como "sin dato" (null).
function limpiar(tabla, datos) {
  const permitidas = COLUMNAS[tabla];
  const limpio = {};
  for (const [clave, valor] of Object.entries(datos || {})) {
    if (permitidas && !permitidas.includes(clave)) continue;
    limpio[clave] = typeof valor === "string" && valor.trim() === "" ? null : valor;
  }
  return limpio;
}

// filtros: { columna: valor } (igual a).
export async function listar(tabla, { select = "*", orden, descendente = false, filtros = {} } = {}) {
  let consulta = supabase.from(tabla).select(select);
  for (const [columna, valor] of Object.entries(filtros)) consulta = consulta.eq(columna, valor);
  if (orden) consulta = consulta.order(orden, { ascending: !descendente });
  return revisar(await consulta) || [];
}

export async function crear(tabla, datos) {
  return revisar(await supabase.from(tabla).insert(limpiar(tabla, datos)).select().single());
}

export async function actualizar(tabla, id, datos) {
  return revisar(await supabase.from(tabla).update(limpiar(tabla, datos)).eq("id", id).select().single());
}

// Crear o actualizar con el mismo formulario.
export function guardar(tabla, id, datos) {
  return id ? actualizar(tabla, id, datos) : crear(tabla, datos);
}

// Guarda varias filas de una vez; si ya existen (según "conflicto"), las actualiza.
export async function guardarVarias(tabla, filas, conflicto) {
  if (!filas.length) return [];
  const limpias = filas.map((f) => limpiar(tabla, f));
  return revisar(await supabase.from(tabla).upsert(limpias, { onConflict: conflicto }).select());
}

export async function eliminar(tabla, id) {
  revisar(await supabase.from(tabla).delete().eq("id", id));
}

export async function eliminarVarias(tabla, ids) {
  if (!ids.length) return;
  revisar(await supabase.from(tabla).delete().in("id", ids));
}

export async function rpc(nombre, argumentos) {
  return revisar(await supabase.rpc(nombre, argumentos));
}

// Llama a una Edge Function con la sesión; si falla, lanza el mensaje que ella explica.
export async function funcion(nombre, cuerpo) {
  const { data, error } = await supabase.functions.invoke(nombre, { body: cuerpo });
  if (error) {
    const detalle = await error.context?.json?.().catch(() => null);
    throw new Error(detalle?.error || "No se pudo completar la acción. Inténtalo de nuevo.");
  }
  return data;
}

// Programa de una reunión con sus canciones, o null.
export async function programaDeReunion(servicioId) {
  const [programa] = await listar("programaciones_servicio", {
    select: "*, canciones_programacion(*)", filtros: { servicio_id: Number(servicioId) },
  });
  return programa || null;
}

// Personas para elegir o mostrar en otras secciones (sin correo, teléfono ni notas).
export const directorioMiembros = () => rpc("miembros_directorio").then((d) => d || []);

// Reuniones con su tipo, de la más antigua a la más nueva.
export const listarReuniones = () =>
  listar("servicios", { select: "*, tipos_servicio(nombre, dirigido_a)", orden: "fecha" });

// Niños para asistencia y calificaciones (sin datos médicos).
export const listaNinos = () => rpc("ninos_lista").then((d) => d || []);

// Agrega a cada fila el objeto de la persona (o lo que sea) que señala su campo, como hacía la API anterior.
export function unir(filas, campo, lista, como) {
  const porId = new Map(lista.map((x) => [x.id, x]));
  return filas.map((f) => ({ ...f, [como]: porId.get(f[campo]) || null }));
}
